'use client';

import { useCallback, useMemo, useState } from 'react';

export interface UseSelectionOptions {
  /** Modus initial aktiv (z. B. wenn die Auswahl direkt bereitstehen soll). Default: false. */
  initialSelectMode?: boolean;
  /** Vorausgewählte IDs (selten gebraucht). */
  initialSelected?: Iterable<string>;
}

export interface UseSelection {
  /** Auswahlmodus aktiv (steuert „Alle auswählen"-Control + SelectionBar `open`). */
  selectMode: boolean;
  /** Auswahl als Array — memoisiert, für die `.map()`-Payloads der Views. */
  selectedIds: string[];
  /** Roh-Set für O(1)-Checks in Render-Schleifen. */
  selectedSet: ReadonlySet<string>;
  count: number;
  isSelected: (id: string) => boolean;

  // Auswahl-Mutationen
  toggle: (id: string) => void;
  /** Persistenter Toggle: sind ALLE `ids` gewählt → entfernt sie; sonst Union. */
  toggleAll: (ids: readonly string[]) => void;
  /** Fügt `ids` hinzu (Union). */
  selectAll: (ids: readonly string[]) => void;
  /** Hebt nur die übergebenen `ids` auf (z. B. nur die sichtbare Seite). */
  deselect: (ids: readonly string[]) => void;
  /** Leert die Auswahl komplett. */
  deselectAll: () => void;
  /** Ersetzt die Auswahl komplett (z. B. async „alle Seiten" laden → setSelected). */
  setSelected: (ids: Iterable<string>) => void;
  /** Alias zu `deselectAll`. */
  clear: () => void;

  // Ableitungen relativ zu einer Kandidatenliste
  allSelected: (ids: readonly string[]) => boolean;
  someSelected: (ids: readonly string[]) => boolean;

  // Modus
  enter: () => void;
  exit: () => void;

  // Async-Flag (z. B. Belege cross-page select-all) — vom View gesteuert.
  selectAllLoading: boolean;
  setSelectAllLoading: (loading: boolean) => void;
}

/**
 * Geteilter Mehrfachauswahl-State — einheitlich für Belege, Favoriten, Projekte
 * und Artikelsuche. Provider-frei (kein Apollo-Import): async Cross-Page-Selects
 * bleiben in der jeweiligen View und nutzen `setSelected` + `setSelectAllLoading`.
 *
 * Intern `Set<string>` (O(1)-Checks, freie Dedup über Seiten hinweg); nach außen
 * wird `selectedIds` als memoisiertes Array bereitgestellt.
 */
export function useSelection(options: UseSelectionOptions = {}): UseSelection {
  const { initialSelectMode = false, initialSelected } = options;

  const [selectMode, setSelectMode] = useState(initialSelectMode);
  const [selectedSet, setSelectedSet] = useState<Set<string>>(
    () => new Set(initialSelected ?? []),
  );
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  const selectedIds = useMemo(() => Array.from(selectedSet), [selectedSet]);
  const count = selectedSet.size;

  const isSelected = useCallback(
    (id: string) => selectedSet.has(id),
    [selectedSet],
  );

  const toggle = useCallback((id: string) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Kern-Fix: persistenter Toggle. Sind ALLE Kandidaten bereits gewählt, werden
  // sie entfernt (= wieder abwählen); sonst werden alle dazugenommen (Union).
  const toggleAll = useCallback((ids: readonly string[]) => {
    setSelectedSet((prev) => {
      const everySelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (everySelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const selectAll = useCallback((ids: readonly string[]) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const deselect = useCallback((ids: readonly string[]) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const deselectAll = useCallback(() => setSelectedSet(new Set()), []);

  const setSelected = useCallback(
    (ids: Iterable<string>) => setSelectedSet(new Set(ids)),
    [],
  );

  const allSelected = useCallback(
    (ids: readonly string[]) =>
      ids.length > 0 && ids.every((id) => selectedSet.has(id)),
    [selectedSet],
  );

  const someSelected = useCallback(
    (ids: readonly string[]) =>
      ids.some((id) => selectedSet.has(id)) &&
      !ids.every((id) => selectedSet.has(id)),
    [selectedSet],
  );

  const enter = useCallback(() => setSelectMode(true), []);
  const exit = useCallback(() => {
    setSelectMode(false);
    setSelectedSet(new Set());
  }, []);

  return {
    selectMode,
    selectedIds,
    selectedSet,
    count,
    isSelected,
    toggle,
    toggleAll,
    selectAll,
    deselect,
    deselectAll,
    setSelected,
    clear: deselectAll,
    allSelected,
    someSelected,
    enter,
    exit,
    selectAllLoading,
    setSelectAllLoading,
  };
}
